import React, { useState, useEffect } from 'react'
import LightboxModal from './LightboxModal'

export default function GallerySection({ reference, title, description }) {
  const [galleryItem, setGalleryItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    // Fetch from CMS v2 content endpoint or plain content list
    fetch(`https://api.dev.auxo.dk/public/content/galleri`)
      .then((res) => res.json())
      .then((data) => {
        let items = []
        if (Array.isArray(data)) {
          items = data
        } else if (data && Array.isArray(data.items)) {
          items = data.items
        }

        const matched = items.find(
          (item) => item.reference === reference || item.slug === reference
        )
        if (matched) {
          setGalleryItem(matched)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Kunne ikke hente galleri fra CMS:', err)
        setLoading(false)
      })
  }, [reference])

  if (loading) {
    return (
      <div className="py-12 text-center text-[#516859]">
        <div className="inline-flex items-center gap-2">
          <i className="fa-regular fa-[#285F41] fa-circle-notch animate-spin text-lg" aria-hidden="true"></i>
          <span>Indlæser galleri...</span>
        </div>
      </div>
    )
  }

  const images = galleryItem?.images || []
  if (images.length === 0) return null

  const displayTitle = title || galleryItem?.title || 'Billedgalleri'
  const displayDesc = description || galleryItem?.description

  return (
    <section className="py-16 bg-[#F4F7F4] border-y border-[#E2ECE4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[#3A684D] bg-[#E1EDE4] px-3.5 py-1.5 rounded-full inline-block">
            Galleri
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#285F41]">
            {displayTitle}
          </h2>
          {displayDesc && (
            <p className="text-sm text-[#4A5D50] leading-relaxed">
              {displayDesc}
            </p>
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((imgUrl, index) => {
            // Apply Cloudflare image optimization CDN
            const optimizedUrl = imgUrl.startsWith('https://assets.auxodev.dk/')
              ? imgUrl.replace(
                  'https://assets.auxodev.dk/',
                  'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/'
                )
              : imgUrl

            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setLightboxIndex(index)
                  setLightboxOpen(true)
                }}
                className="group relative h-64 sm:h-72 rounded-2xl overflow-hidden bg-[#E2ECE4] border border-[#D5E3D8] shadow-sm hover:shadow-md transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#285F41] cursor-pointer"
                aria-label={`Åbn galleribillede ${index + 1} i stor visning`}
              >
                <img
                  src={optimizedUrl}
                  alt={`Galleri resultat ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#285F41]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-white text-xs font-semibold flex items-center gap-2 bg-[#285F41]/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/20">
                    <i className="fa-regular fa-expand text-xs" aria-hidden="true"></i>
                    <span>Klik for stor visning</span>
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(newIdx) => setLightboxIndex(newIdx)}
      />
    </section>
  )
}
